import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X, Package, Trash2, Check } from "lucide-react";
import { SelectedOrderProductData } from "@/types/notifications";

interface InkoopProductReviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: SelectedOrderProductData[];
  onConfirm: (products: SelectedOrderProductData[]) => void;
  onCancel: () => void;
}

const InkoopProductReview: React.FC<InkoopProductReviewProps> = ({
  open,
  onOpenChange,
  products,
  onConfirm,
  onCancel,
}) => {
  const [editableProducts, setEditableProducts] = useState<SelectedOrderProductData[]>([]);

  useEffect(() => {
    if (open) {
      setEditableProducts(products.map((p) => ({ ...p })));
    }
  }, [open, products]);

  const handleRemove = (sku: string) => {
    setEditableProducts((prev) => prev.filter((p) => p.sku !== sku));
  };

  const handleQuantityChange = (sku: string, quantity: number) => {
    setEditableProducts((prev) =>
      prev.map((p) => (p.sku === sku ? { ...p, quantity: Math.max(1, quantity) } : p))
    );
  };

  const handleColorChange = (sku: string, color: string) => {
    setEditableProducts((prev) =>
      prev.map((p) => (p.sku === sku ? { ...p, color: color || null } : p))
    );
  };

  const handleConfirm = () => {
    if (editableProducts.length === 0) {
      onCancel();
      return;
    }
    onConfirm(editableProducts);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Inkoop producten controleren
          </DialogTitle>
          <DialogDescription>
            Controleer de producten voordat de inkoop e-mail wordt verzonden. Je kunt aantal en kleur aanpassen of producten verwijderen.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-2">
          {editableProducts.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              Geen producten meer. De inkoop wordt geannuleerd.
            </div>
          ) : (
            editableProducts.map((product) => (
              <div
                key={product.sku}
                className="flex items-start gap-3 p-3 rounded-lg border bg-card"
              >
                {product.imageUrl && (
                  <img
                    src={product.imageUrl}
                    alt={product.productTitle}
                    className="w-12 h-12 rounded object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{product.productTitle}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-[10px] h-5">
                          {product.sku}
                        </Badge>
                        {product.variantTitle && (
                          <span className="text-xs text-muted-foreground truncate">
                            {product.variantTitle}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemove(product.sku)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="space-y-1">
                      <label className="text-[11px] text-muted-foreground font-medium">
                        Aantal
                      </label>
                      <Input
                        type="number"
                        min={1}
                        value={product.quantity}
                        onChange={(e) =>
                          handleQuantityChange(product.sku, parseInt(e.target.value) || 1)
                        }
                        className="h-8 w-20 text-sm"
                      />
                    </div>
                    <div className="space-y-1 flex-1">
                      <label className="text-[11px] text-muted-foreground font-medium">
                        Kleur
                      </label>
                      <Input
                        type="text"
                        value={product.color || ""}
                        onChange={(e) => handleColorChange(product.sku, e.target.value)}
                        placeholder="Geen kleur"
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onCancel}>
            Annuleren
          </Button>
          <Button onClick={handleConfirm} disabled={editableProducts.length === 0}>
            <Check className="h-4 w-4 mr-1" />
            Bevestigen ({editableProducts.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InkoopProductReview;
